import React, { useState, useRef } from "react";
import { useTheme } from "../../theme";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Image,
  TextInput,
  Platform,
  Pressable,
  Animated,
  Modal,
  Alert,
  Dimensions,
  ScrollView,
} from "react-native";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import { setPulseImage } from "../../store/slices/pulseCheckSlice";
import { createApiClient } from "../../api/apiClient";
import { launchImageLibrary, launchCamera } from "react-native-image-picker";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import LHProgressBar from "../../components/LHProgressBar";
import { useToast } from "../../components/LhToastContainer";
import FontFamily from "../../constants/fonts";

// Helper to check for web/document safely
const isWeb =
  typeof (globalThis as any).document !== "undefined" && Platform.OS === "web";

// Add a constant to determine if the description is truly empty
const EMPTY_TEXT = " ";

interface ImageData {
  imageUri: string | null;
  description: string;
}

interface PulseCheckActivityImageUploadProps {
  visible: boolean;
  onClose: () => void;
  activityId: string;
  activityLabel: string;
}

const PulseCheckActivityImageUpload: React.FC<
  PulseCheckActivityImageUploadProps
> = ({ visible, onClose, activityId, activityLabel }) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const descHeaderColor = isDark ? colors.text.primary : '#6c757d';
  const descInputBg = isDark ? colors.input.background : '#fff';
  const descInputColor = isDark ? colors.text.primary : '#495057';
  const placeholderColor = isDark ? colors.text.secondary : '#c0c0c0';
  const modalSurfaceColor = isDark ? colors.background.surface : 'white';
  const borderColor = isDark ? colors.border.default : '#D8E7FB';
  const neutralTextColor = isDark ? colors.text.primary : '#3A3A3A';
  const dropZoneBg = isDark ? '#2A2A2A' : '#F5F9FF';
  const dropZoneBorder = isDark ? colors.border.default : '#D8E7FB';
  const uploadButtonBg = isDark ? '#2A2A2A' : '#FBFCFE';
  const uploadButtonTextColor = isDark ? colors.brand.primary : '#005CBC';

  const dispatch = useDispatch();
  const toast = useToast();
  const pulseImages = useSelector(
    (state: RootState) => state.pulseCheck.pulseImages
  );
  const pulseState = useSelector(
    (state: RootState) => state.pulseCheck.pulseState
  );

  // Component state
  const [images, setImages] = useState<ImageData[]>([
    { imageUri: null, description: activityLabel },
    { imageUri: null, description: activityLabel },
  ]);

  // Reset state and load images when activityId changes or component becomes visible
  React.useEffect(() => {
    if (visible) {
      // Find existing images for this activity
      const existingImages = pulseImages.filter(
        (img) => img.observationId === activityId
      );
      // Find image with imageId="1" and imageId="2" specifically
      const firstImage = existingImages.find((img) => img.imageId === "1");
      const secondImage = existingImages.find((img) => img.imageId === "2");
      // If slot 1 has an image, but slot 2 does not, clear slot 2's description
      // If slot 2 has an image, but slot 1 does not, clear slot 1's description
      const slot1HasImage = !!(
        firstImage?.imagePreview || firstImage?.imageBase64
      );
      const slot2HasImage = !!(
        secondImage?.imagePreview || secondImage?.imageBase64
      );

      setImages([
        {
          imageUri: firstImage?.imagePreview || firstImage?.imageBase64 || null,
          description:
            !slot1HasImage && slot2HasImage
              ? EMPTY_TEXT
              : firstImage?.description !== undefined
              ? firstImage.description
              : activityLabel,
        },
        {
          imageUri:
            secondImage?.imagePreview || secondImage?.imageBase64 || null,
          description:
            !slot2HasImage && slot1HasImage
              ? EMPTY_TEXT
              : secondImage?.description !== undefined
              ? secondImage.description
              : activityLabel,
        },
      ]);

      // Reset other state values
      setErrorMessages([null, null]);
      setUploadProgress([0, 0]);
      setUploadingStates([false, false]);
    }
  }, [activityId, visible, pulseImages, activityLabel]);
  const [errorMessages, setErrorMessages] = useState<(string | null)[]>([
    null,
    null,
  ]);
  const [uploadProgress, setUploadProgress] = useState<number[]>([0, 0]);
  const [uploadingStates, setUploadingStates] = useState<boolean[]>([
    false,
    false,
  ]);

  // Create animated values for each image
  const iconScales = useRef([
    new Animated.Value(1),
    new Animated.Value(1),
  ]).current;
  const circleScales = useRef([
    new Animated.Value(1),
    new Animated.Value(1),
  ]).current;
  const borderColors = useRef([
    new Animated.Value(0),
    new Animated.Value(0),
  ]).current;

  // Function to handle hover state for web with smooth animations
  const handleHoverIn = (index: number) => {
    if (isWeb) {
      Animated.parallel([
        Animated.timing(iconScales[index], {
          toValue: 1.2,
          duration: 250,
          useNativeDriver: true,
        }),
        Animated.timing(circleScales[index], {
          toValue: 1.1,
          duration: 250,
          useNativeDriver: true,
        }),
        Animated.timing(borderColors[index], {
          toValue: 1,
          duration: 250,
          useNativeDriver: false,
        }),
      ]).start();
    }
  };

  // Function to handle hover out state for web
  const handleHoverOut = (index: number) => {
    if (isWeb) {
      Animated.parallel([
        Animated.timing(iconScales[index], {
          toValue: 1,
          duration: 250,
          useNativeDriver: true,
        }),
        Animated.timing(circleScales[index], {
          toValue: 1,
          duration: 250,
          useNativeDriver: true,
        }),
        Animated.timing(borderColors[index], {
          toValue: 0,
          duration: 250,
          useNativeDriver: false,
        }),
      ]).start();
    }
  };

  // Helper function to handle mobile image selection (camera or gallery)
  const handleMobileImageSelection = async (
    index: number,
    source: "camera" | "gallery"
  ) => {
    const options = {
      mediaType: "photo" as const,
      quality: 1 as const,
    };

    let result;
    try {
      if (source === "camera") {
        result = await launchCamera(options);
      } else {
        result = await launchImageLibrary(options);
      }
    } catch (error: any) {
      console.error("Error launching camera/library:", error);

      // Handle permission errors gracefully
      if (error.code === "camera_unavailable" || error.code === "permission") {
        Alert.alert(
          "Permission Required",
          source === "camera"
            ? "Camera access is required to take photos. Please enable camera permissions in Settings."
            : "Photo library access is required. Please enable photo permissions in Settings.",
          [{ text: "OK" }]
        );
      } else {
        Alert.alert(
          "Error",
          `Unable to ${
            source === "camera" ? "open camera" : "access photo library"
          }. Please try again.`,
          [{ text: "OK" }]
        );
      }

      // Reset uploading state
      setUploadingStates((prev) => {
        const newStates = [...prev];
        newStates[index] = false;
        return newStates;
      });
      return;
    }

    if (result.didCancel) {
      setUploadingStates((prev) => {
        const newStates = [...prev];
        newStates[index] = false;
        return newStates;
      });
      return;
    }

    if (result.errorMessage) {
      console.error("Image picker error:", result.errorMessage);
      Alert.alert("Error", result.errorMessage, [{ text: "OK" }]);
      setUploadingStates((prev) => {
        const newStates = [...prev];
        newStates[index] = false;
        return newStates;
      });
      return;
    }

    if (result.assets && result.assets.length > 0) {
      const asset = result.assets[0];

      // Set uploading state after we have a valid file
      setUploadingStates((prev) => {
        const newStates = [...prev];
        newStates[index] = true;
        return newStates;
      });

      if (asset.fileSize && asset.fileSize > 5 * 1024 * 1024) {
        setErrorMessages((prev) => {
          const newErrors = [...prev];
          newErrors[index] = "File size exceeds 10MB limit";
          return newErrors;
        });
        setUploadingStates((prev) => {
          const newStates = [...prev];
          newStates[index] = false;
          return newStates;
        });
        return;
      }
      // Don't set upload progress until API call starts
      const formData = new FormData();
      const fileName = asset.fileName || "image.jpg";
      formData.append("image", {
        uri: asset.uri,
        name: fileName,
        type: asset.type || "image/jpeg",
      } as any);
      formData.append("containerName", "observations"); // Add containerName for PulseCheck

      try {
        const api = createApiClient();
        const response = await api.post("/common/images/upload", formData, {
          headers: {
            Accept: "application/json",
            // Do NOT set Content-Type, let axios set it for FormData
          },
          onUploadProgress: (progressEvent: any) => {
            if (progressEvent && progressEvent.total) {
              // Cap progress at 50% until API call is complete
              const progress = Math.min(
                0.5,
                progressEvent.loaded / progressEvent.total
              );
              setUploadProgress((prev) => {
                const arr = [...prev];
                // First progress event - set to at least 0.05 to show it started
                if (prev[index] === 0) {
                  arr[index] = Math.max(0.05, progress);
                } else {
                  arr[index] = progress;
                }
                return arr;
              });
            }
          },
        });

        if (response && response.data && response.data.success === true) {
          const { imageUrl, fileNameResp } = response.data.data;

          // Update local state immediately to show image
          const updatedImages = [...images];
          updatedImages[index].imageUri = imageUrl;
          setImages(updatedImages);

          // Update Redux state with the new image
          dispatch(
            setPulseImage({
              imageId: `${index + 1}`,
              imageBase64: fileNameResp,
              imagePreview: imageUrl,
              description:
                images[index].description === "" ||
                images[index].description === EMPTY_TEXT
                  ? EMPTY_TEXT
                  : images[index].description,
              observationId: activityId,
            })
          );

          // Show success toast notification
          toast.showToast({
            header: "Success",
            subtext: "Image uploaded successfully",
            variant: "success",
          });

          // Set progress to 100% only after successful response
          setUploadProgress((prev) => {
            const arr = [...prev];
            arr[index] = 1;
            return arr;
          });
          // Show 100% briefly before hiding progress bar
          await new Promise((resolve) => setTimeout(resolve, 500));
        } else {
          // Get error message from response or use default
          const errorMessage =
            response?.data?.message || "Failed to upload image";
          const errorDetail = response?.data?.error || "Unknown error";

          // Set error message in state
          setErrorMessages((prev) => {
            const newErrors = [...prev];
            newErrors[index] = "Unable to upload image";
            return newErrors;
          });

          // Show error toast notification
          toast.showToast({
            header: "Error Uploading Image",
            subtext: `${errorMessage}: ${errorDetail}`,
            variant: "error",
          });
        }
      } catch (e: any) {
        console.error("Image upload error:", e);

        // Set error message in state
        setErrorMessages((prev) => {
          const newErrors = [...prev];
          newErrors[index] = "Unable to upload image";
          return newErrors;
        });

        // Show error toast notification
        toast.showToast({
          header: "Error Uploading Image",
          subtext: e?.message || "An unexpected error occurred",
          variant: "error",
        });
      } finally {
        // Only reset progress and uploading state if there was no error
        if (!errorMessages[index]) {
          setUploadProgress((prev) => {
            const arr = [...prev];
            arr[index] = 0;
            return arr;
          });

          setUploadingStates((prev) => {
            const newStates = [...prev];
            newStates[index] = false;
            return newStates;
          });
        } else {
          // If there was an error, we still need to allow retry
          setUploadingStates((prev) => {
            const newStates = [...prev];
            newStates[index] = false;
            return newStates;
          });
        }
      }
    } else {
      setErrorMessages((prev) => {
        const newErrors = [...prev];
        newErrors[index] = "Unable to upload image";
        return newErrors;
      });
      setUploadingStates((prev) => {
        const newStates = [...prev];
        newStates[index] = false;
        return newStates;
      });
    }
  };

  const handleImageUpload = async (index: number) => {
    // Prevent upload if in completed state or already uploading
    if (uploadingStates[index] || pulseState === "completed") {
      return;
    }

    // Reset error for this image
    setErrorMessages((prev) => {
      const newErrors = [...prev];
      newErrors[index] = null;
      return newErrors;
    });

    if (isWeb) {
      const input = (globalThis as any).document.createElement("input");
      input.type = "file";
      input.accept = "image/*";
      input.onchange = async (event: any) => {
        const file = event.target.files[0];
        if (file) {
          // Only set uploading state after file is selected
          setUploadingStates((prev) => {
            const newStates = [...prev];
            newStates[index] = true;
            return newStates;
          });

          if (file.size > 10 * 1024 * 1024) {
            setErrorMessages((prev) => {
              const newErrors = [...prev];
              newErrors[index] = "File size exceeds 10MB limit";
              return newErrors;
            });
            setUploadingStates((prev) => {
              const newStates = [...prev];
              newStates[index] = false;
              return newStates;
            });
            return;
          }
          // Don't set upload progress until API call starts
          const formData = new FormData();
          formData.append("image", file);
          formData.append("containerName", "observations"); // Add containerName for PulseCheck

          try {
            const api = createApiClient();
            const response = await api.post("/common/images/upload", formData, {
              headers: {
                Accept: "application/json",
                // Do NOT set Content-Type, let browser/axios set it for FormData
              },
              onUploadProgress: (progressEvent: any) => {
                if (progressEvent && progressEvent.total) {
                  // Cap progress at 50% until API call is complete
                  const progress = Math.min(
                    0.5,
                    progressEvent.loaded / progressEvent.total
                  );
                  setUploadProgress((prev) => {
                    const arr = [...prev];
                    // First progress event - set to at least 0.05 to show it started
                    if (prev[index] === 0) {
                      arr[index] = Math.max(0.05, progress);
                    } else {
                      arr[index] = progress;
                    }
                    return arr;
                  });
                }
              },
            });
            if (response && response.data && response.data.success === true) {
              const { imageUrl, fileName } = response.data.data;
              // Update local state immediately to show image
              const updatedImages = [...images];
              updatedImages[index].imageUri = imageUrl;
              setImages(updatedImages);

              // Update Redux state with the new image
              dispatch(
                setPulseImage({
                  imageId: `${index + 1}`, // 1 or 2 based on which upload area was used
                  imageBase64: fileName, // Store the file name instead of the URL
                  imagePreview: imageUrl, // Store the image URL for preview
                  description:
                    images[index].description === ""
                      ? EMPTY_TEXT
                      : images[index].description,
                  observationId: activityId,
                })
              );

              // Show success toast notification
              toast.showToast({
                header: "Success",
                subtext: "Image uploaded successfully",
                variant: "success",
              });

              // Set progress to 100% only after successful response
              setUploadProgress((prev) => {
                const arr = [...prev];
                arr[index] = 1;
                return arr;
              });
              // Show 100% briefly before hiding progress bar
              await new Promise((resolve) => setTimeout(resolve, 500));
            } else {
              // Get error message from response or use default
              const errorMessage =
                response?.data?.message || "Failed to upload image";
              const errorDetail = response?.data?.error || "Unknown error";

              // Set error message in state
              setErrorMessages((prev) => {
                const newErrors = [...prev];
                newErrors[index] = "Unable to upload image";
                return newErrors;
              });

              // Show error toast notification
              toast.showToast({
                header: "Error Uploading Image",
                subtext: `${errorMessage}: ${errorDetail}`,
                variant: "error",
              });
            }
          } catch (e: any) {
            console.error("Image upload error:", e);

            // Set error message in state
            setErrorMessages((prev) => {
              const newErrors = [...prev];
              newErrors[index] = "Unable to upload image";
              return newErrors;
            });

            // Show error toast notification
            toast.showToast({
              header: "Error Uploading Image",
              subtext: e?.message || "An unexpected error occurred",
              variant: "error",
            });
          } finally {
            // Only reset progress and uploading state if there was no error
            if (!errorMessages[index]) {
              setUploadProgress((prev) => {
                const arr = [...prev];
                arr[index] = 0;
                return arr;
              });

              setUploadingStates((prev) => {
                const newStates = [...prev];
                newStates[index] = false;
                return newStates;
              });
            } else {
              // If there was an error, we still need to allow retry
              setUploadingStates((prev) => {
                const newStates = [...prev];
                newStates[index] = false;
                return newStates;
              });
            }
          }
        }
        // If no file was selected, do nothing - the button remains enabled
      };
      input.click();
    } else {
      // Mobile: Show action sheet with Take Photo and Choose from Gallery options
      Alert.alert("Add Image", "How would you like to add an image?", [
        {
          text: "Take Photo",
          onPress: () => handleMobileImageSelection(index, "camera"),
        },
        {
          text: "Choose from Gallery",
          onPress: () => handleMobileImageSelection(index, "gallery"),
        },
        {
          text: "Cancel",
          style: "cancel",
          onPress: () => {
            // Reset uploading state when cancelled
            setUploadingStates((prev) => {
              const newStates = [...prev];
              newStates[index] = false;
              return newStates;
            });
          },
        },
      ]);
    }
  };

  const handleDescriptionChange = (index: number, text: string) => {
    // Prevent changes if in completed state
    if (pulseState === "completed") {
      return;
    }

    const updatedImages = [...images];
    updatedImages[index].description = text;
    setImages(updatedImages);

    // Update Redux if the image exists
    if (images[index].imageUri) {
      // Find existing image data for this activity and index
      const existingImage = pulseImages.find(
        (img) =>
          img.observationId === activityId && img.imageId === `${index + 1}`
      );

      dispatch(
        setPulseImage({
          imageId: `${index + 1}`,
          imageBase64: existingImage?.imageBase64 || "",
          imagePreview: images[index].imageUri,
          description: text === "" ? EMPTY_TEXT : text,
          observationId: activityId,
        })
      );
    }
  };

  // Small-device detection (match SparcWalk logic)
  const isSmallMobileDevice = () => {
    const windowWidth = Dimensions.get("window").width;
    const windowHeight = Dimensions.get("window").height;
    return (
      (Platform.OS === "ios" || Platform.OS === "android") &&
      (windowWidth < 380 || windowHeight < 700)
    );
  };

  const isCompactMode = isSmallMobileDevice();

  const getImageContainerHeight = () => {
    if (isSmallMobileDevice()) {
      return 110;
    }
    return 150;
  };

  const imageContainerHeight = getImageContainerHeight();
  // Detect tablet / iPad widths so we can use two-column layout like SparcWalk
  const windowWidth = Dimensions.get("window").width;
  const isTablet = windowWidth >= 768;
  // Treat web as large-screen only when the window width is >= 768
  const isLargeScreen = isTablet || (isWeb && windowWidth >= 768);

  return (
    <Modal
      visible={visible}
      transparent={true}
      animationType="fade"
      onRequestClose={onClose}
      testID="image-upload-modal"
    >
      <View style={styles.modalOverlay}>
        <View style={[styles.modalContent, isCompactMode && styles.modalContentCompact, { backgroundColor: modalSurfaceColor, borderColor }] }>
          <View style={styles.modalHeader}>
            <Text style={[styles.modalTitle, isCompactMode && styles.modalTitleCompact, { color: colors.text.primary }]}>Upload Images for {activityLabel}</Text>
            <TouchableOpacity onPress={onClose} style={styles.closeButton}>
              <MaterialIcons name="close" size={24} color={isDark ? colors.text.primary : "#333"} />
            </TouchableOpacity>
          </View>

          <ScrollView
            style={{ backgroundColor: modalSurfaceColor }}
            contentContainerStyle={isCompactMode ? { padding: 8, backgroundColor: modalSurfaceColor } : { padding: 16, backgroundColor: modalSurfaceColor }}
            showsVerticalScrollIndicator={true}
          >
            {images.map((image, index) => {
              const hasImage = !!image.imageUri;
              const isCompleted = pulseState === "completed";

              return (
                <View
                  key={index}
                  style={[
                    styles.observationContainer,
                    isLargeScreen && styles.observationContainerRow, // row on tablet/web
                    isCompactMode && styles.observationContainerCompact, // column on small phones
                  ]}
                >
                  <View
                    style={[
                      styles.uploadContainer,
                      { height: imageContainerHeight },
                      isCompleted && styles.disabledContainer,
                      isCompactMode && styles.uploadContainerCompact,
                    ]}
                  >
                    {hasImage ? (
                      <Pressable
                        style={[styles.imagePreviewContainer, isCompleted && styles.disabledContainer]}
                        onPress={() => !isCompleted && handleImageUpload(index)}
                        disabled={isCompleted}
                      >
                        <View style={styles.imageContainer}>
                          <Image
                            source={image.imageUri ? { uri: image.imageUri } : undefined}
                            style={styles.fullImage}
                          />
                        </View>
                        {!isCompleted && (
                          <View style={styles.imageOverlay}>
                            <MaterialIcons name="edit" size={24} color="white" />
                            <Text style={styles.overlayText}>Tap to change</Text>
                          </View>
                        )}
                      </Pressable>
                    ) : (
                      <View style={styles.attachmentsContainer}>
                        <View
                          style={[
                            styles.dropZone,
                            { backgroundColor: dropZoneBg, borderColor: dropZoneBorder },
                            uploadingStates[index] && styles.dropZoneUploading,
                            errorMessages[index] && styles.dropZoneError,
                          ]}
                        >
                          {uploadingStates[index] && uploadProgress[index] > 0 ? (
                            <View style={styles.progressContainer}>
                              <LHProgressBar progress={uploadProgress[index]} height={16} />
                              <Text style={styles.progressText}>
                                {uploadProgress[index] >= 0.99 ? "Processing..." : `${Math.round(uploadProgress[index] * 100)}%`}
                              </Text>
                            </View>
                          ) : (
                            <>
                              <View style={styles.iconLabelContainer}>
                                <View style={styles.iconContainer}>
                                  <MaterialIcons name="camera-alt" size={24} color={errorMessages[index] ? 'red' : neutralTextColor} />
                                </View>
                                <Text style={[styles.dragDropText, { color: neutralTextColor }, errorMessages[index] && styles.errorText]}> 
                                  {errorMessages[index] || 'Drag and Drop your file here'}
                                </Text>
                                <Text style={[styles.dragDropText, { color: neutralTextColor }]}>(only .jpg,.png)</Text>
                              </View>
                              <View style={{ marginTop: 8 }}>
                                <Pressable
                                  style={[styles.uploadButton, { backgroundColor: uploadButtonBg, borderColor: colors.brand.primary }, isCompleted && styles.disabledButton]}
                                  onPress={() => !isCompleted && handleImageUpload(index)}
                                  disabled={isCompleted || uploadingStates[index]}
                                >
                                  <View style={styles.buttonContent}>
                                    <View style={styles.uploadIconContainer}>
                                      <MaterialIcons name="file-upload" size={14} color={isCompleted ? '#999' : uploadButtonTextColor} />
                                    </View>
                                    <Text style={[styles.uploadButtonText, { color: uploadButtonTextColor }, isCompleted && styles.disabledButtonText]}>
                                      {uploadingStates[index] ? 'Uploading...' : 'Upload'}
                                    </Text>
                                  </View>
                                </Pressable>
                              </View>
                            </>
                          )}
                        </View>
                      </View>
                    )}
                  </View>

                  <View style={[styles.descriptionContainer, isCompactMode && styles.descriptionContainerCompact, isTablet && styles.descriptionContainerTablet]}>
                    <Text style={[styles.descriptionHeader, { color: descHeaderColor }]}>DESCRIPTION</Text>
                    <TextInput
                      style={[styles.descriptionInput, { backgroundColor: descInputBg, color: descInputColor, borderColor: colors.input.border }, pulseState === 'completed' && styles.disabledInput]}
                      placeholder="Add Description..."
                      placeholderTextColor={placeholderColor}
                      multiline
                      value={image.description === EMPTY_TEXT ? '' : image.description}
                      onChangeText={(text) => handleDescriptionChange(index, text)}
                      editable={pulseState !== 'completed'}
                    />
                  </View>
                </View>
              );
            })}

            <View style={styles.actionButtonsRow}>
              <Pressable
                style={[styles.cancelButton, isCompactMode && styles.cancelButtonCompact]}
                onPress={onClose}
                accessibilityLabel="Cancel"
              >
                <Text style={[styles.cancelButtonText, { color: colors.brand.primary }, isCompactMode && styles.cancelButtonTextCompact]}>Cancel</Text>
              </Pressable>

              <Pressable
                style={[styles.proceedButton, isCompactMode && styles.proceedButtonCompact]}
                onPress={onClose}
                accessibilityLabel="Proceed"
              >
                <Text style={[styles.proceedButtonText, isCompactMode && styles.proceedButtonTextCompact]}>Proceed</Text>
              </Pressable>
            </View>
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
  },
  modalContent: {
    backgroundColor: "white",
    borderRadius: 8,
    // Add outer blue border to match SparcWalk look
    borderWidth: 10,
    borderColor: "#D8E7FB",
    padding: 16,
    width: Platform.OS === "web" ? "80%" : "90%",
    maxWidth: 800,
    // Use a slightly smaller maxHeight on mobile to match SparcWalk modal
    maxHeight: Platform.OS === "ios" || Platform.OS === "android" ? "80%" : "90%",
  },
  modalContentCompact: {
    padding: 8,
    width: '90%',
  },
  modalTitleCompact: {
    fontSize: 16,
  },
  modalHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: "bold",
  },
  closeButton: {
    padding: 8,
  },
  container: {
    padding: 16,
    backgroundColor: "#fff",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#e9ecef",
  },
  observationContainer: {
    // Default to column for mobile phones (stacked). Tablet/web will use
    // observationContainerRow to get a two-column layout like SparcWalk.
    flexDirection: 'column',
    alignItems: 'stretch',
    justifyContent: 'center',
    marginBottom: 20,
    position: 'relative',
  },
  observationContainerRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'center',
  },
  observationContainerCompact: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  uploadContainer: {
    // Use 35% column width on larger screens (web / iPad / tablet) to
    // mirror SparcWalk. Small phones use uploadContainerCompact to be full-width.
    width: Platform.OS === "web" || Dimensions.get("window").width >= 768 ? '35%' : '100%',
    height: 150,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
    marginBottom: 0,
    overflow: 'visible',
    position: 'relative',
  },
  uploadContainerCompact: {
    width: '100%', // Full width for iOS mobile
    height: 110,
    marginRight: 0,
    marginBottom: 16,
  },
  imageContainer: {
    width: "100%",
    height: "100%",
    alignItems: "center",
    justifyContent: "center",
  },
  imagePreviewContainer: {
    borderRadius: 8,
    overflow: 'hidden',
    position: 'relative',
    width: '100%',
    height: '100%',
  },
  fullImage: {
    width: '100%',
    height: '100%',
    resizeMode: 'contain',
  },
  imageOverlay: {
    position: 'absolute',
    bottom: 8,
    right: 8,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    borderRadius: 16,
    paddingHorizontal: 8,
    paddingVertical: 4,
    flexDirection: 'row',
    alignItems: 'center',
  },
  overlayText: {
    color: 'white',
    fontSize: 12,
    marginLeft: 4,
  },
  attachmentsContainer: {
    width: '100%',
  },
  dropZone: {
    width: '100%',
    paddingHorizontal: 16,
    paddingVertical: 16,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 110,
    // Match SparcWalk visual: pale blue background and single solid border
    backgroundColor: '#F5F9FF',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#D8E7FB',
    borderRadius: 8,
  },
  dropZoneUploading: {
    borderColor: '#0066CC',
    backgroundColor: 'rgba(0,102,204,0.04)',
  },
  dropZoneError: {
    borderColor: 'red',
    backgroundColor: 'rgba(255, 235, 235, 0.8)',
  },
  iconLabelContainer: {
    width: '100%',
    alignItems: 'center',
  },
  iconContainer: {
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: -8,
  },
  dragDropText: {
    fontFamily: FontFamily.regular,
    fontWeight: '400',
    fontSize: 12,
    lineHeight: 18,
    textAlign: 'center',
    color: '#3A3A3A',
    width: '100%',
    marginTop: 4,
  },
  uploadButton: {
    height: 24,
    backgroundColor: '#FBFCFE',
    borderWidth: 1,
    borderColor: '#005CBC',
    borderRadius: 24,
    alignSelf: 'stretch',
    paddingHorizontal: 12,
    paddingVertical: 0,
  },
  disabledButton: {
    backgroundColor: '#f2f2f2',
    borderColor: '#999',
  },
  buttonContent: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 2,
    paddingHorizontal: 8,
    paddingVertical: 0,
  },
  uploadIconContainer: {
    width: 16,
    height: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  uploadButtonText: {
    fontFamily: FontFamily.regular,
    fontWeight: '700',
    fontSize: 12,
    lineHeight: 18,
    textAlign: 'center',
    color: '#005CBC',
  },
  disabledButtonText: {
    color: '#999',
  },
  descriptionContainer: {
    flex: Platform.OS === "web" ? 1 : undefined,
    width: Platform.OS === "web" ? undefined : "100%",
    height: 150,
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    marginTop: Platform.OS === "web" ? 0 : 16,
  },
  descriptionContainerCompact: {
    height: 110,
  },
  descriptionContainerTablet: {
    // On tablet/iPad make the description take the remaining width next to upload column
    flex: 1,
    width: undefined,
    height: 150,
    marginTop: 0,
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
  },
  descriptionHeader: {
    fontSize: 12,
    fontWeight: "bold",
    color: "#6c757d",
    marginBottom: 8,
  },
  descriptionInput: {
    width: "100%",
    flex: 1,
    borderWidth: 1,
    borderColor: "#ced4da",
    borderRadius: 8,
    padding: 8,
    fontSize: 14,
    backgroundColor: "#fff",
    textAlignVertical: "top",
  },
  doneButton: {
    backgroundColor: "#0066CC",
    borderRadius: 8,
    padding: 12,
    marginTop: 16,
    alignItems: "center",
  },
  doneButtonText: {
    color: "white",
    fontWeight: "bold",
    fontSize: 16,
  },
  actionButtonsRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 12,
    alignSelf: 'stretch',
    marginTop: 16,
  },
  cancelButton: {
    height: 36,
    paddingHorizontal: 12,
    paddingVertical: 6,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'transparent',
  },
  cancelButtonCompact: {
    paddingVertical: 6,
  },
  cancelButtonText: {
    color: '#005CBC',
    fontWeight: '700',
    fontSize: 14,
  },
  cancelButtonTextCompact: {
    fontSize: 14,
  },
  proceedButton: {
    height: 36,
    backgroundColor: '#005CBC',
    borderRadius: 24,
    paddingHorizontal: 16,
    paddingVertical: 6,
    justifyContent: 'center',
    alignItems: 'center',
  },
  proceedButtonCompact: {
    paddingVertical: 6,
  },
  proceedButtonText: {
    color: '#FFFFFF',
    fontWeight: '700',
    fontSize: 14,
  },
  proceedButtonTextCompact: {
    fontSize: 14,
  },
  fullWidth: {
    width: "100%",
  },
  disabledText: {
    color: "#DDD",
  },
  errorContainer: {
    borderColor: "red",
    backgroundColor: "rgba(255, 235, 235, 0.8)",
  },
  pressableContainer: {
    width: "100%",
    height: "100%",
    justifyContent: "center",
    alignItems: "center",
  },
  progressContainer: {
    width: "96%",
    marginLeft: "2%",
    marginRight: "2%",
    alignItems: "center",
    justifyContent: "center",
  },
  progressText: {
    fontSize: 12,
    marginTop: 8,
  },
  errorBorder: {
    borderColor: "red",
  },
  errorText: {
    color: "red",
  },
  disabledContainer: {
    opacity: 0.6,
    backgroundColor: "#f2f2f2",
    ...(Platform.OS === "web" ? { cursor: "not-allowed" as any } : {}),
  },
  disabledInput: {
    backgroundColor: "#f2f2f2",
    color: "#888888",
  },
});

export default PulseCheckActivityImageUpload;
